import { MarkdownRenderer } from "components/markdown";
import {
  Container,
  ResponsiveText,
} from "modules/marketing-pages/front-page/shared";
import { useTheme } from "@mui/material/styles";
import { Box } from "components/ui/box";
import { Typography } from "components/ui/Typography";
import { serverRoutesConfig } from "client-server-shared/config/routes";
import { PageHeadTags } from "common/head-tags";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "components/translate";
import { isCn, locale } from "client-server-shared/constants";

const body = `
# Terms of Service for WritingTools.AI

#### Updated at 2024-09-02

## 1. Acceptance of Terms

By accessing or using WritingTools.AI, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any part of these terms, you may not use our service.

## 2. Description of Service

WritingTools.AI provides an AI-powered writing assistance platform. The service is provided "AS IS" and "AS AVAILABLE" without any warranty of any kind.

## 3. User Responsibilities

### 3.1 Account Management
You are responsible for maintaining the confidentiality of your account and password. You agree to accept responsibility for all activities that occur under your account.

### 3.2 Prohibited Content
You agree not to use WritingTools.AI to generate adult, age-restricted, sexually-oriented, pornographic content or any material of a lewd and lascivious nature.

### 3.3 Fair Usage Policy
A Fair Usage Policy is implemented to maintain fast service, prevent abuse/overload, and ensure the best possible experience. Violation may result in account suspension or termination (affects <0.1% of users).

## 4. Intellectual Property Rights

### 4.1 Ownership
All content, features, and functionality (including but not limited to information, software, text, displays, images, video, and audio) are the property of WritingTools.AI or its licensors and are protected by copyright, trademark, patent, trade secret, and other intellectual property laws.

### 4.2 Limited License
WritingTools.AI grants you a limited, non-exclusive, non-transferable license to use the website in accordance with these Terms of Service.

### 4.3 Restrictions
You may not sell, rent, lease, or distribute WritingTools.AI's Service. You are prohibited from unauthorized access, monitoring for competitive advantage, introducing viruses or malicious code, copying or modifying WritingTools.AI Property, or altering/removing copyright notices.

## 5. User Submissions

Any content submitted to WritingTools.AI becomes the sole property of WritingTools.AI without compensation or credit to the submitter.

## 6. Third-Party Content and Services

### 6.1 Disclaimer
WritingTools.AI is a distributor, not a publisher, of third-party content and has no editorial control over such content.

### 6.2 Liability
WritingTools.AI is not responsible for third-party content, opinions on linked websites, or third-party services. User access is at their own risk.

## 7. Pricing and Payments

### 7.1 Changes to Pricing
WritingTools.AI reserves the right to change prices and usage policies at any time.

### 7.2 Refund Policy
We offer a free plan for evaluation. No refunds are provided for subscriptions or one-off payments. The Stripe transaction fee is non-refundable.

### 7.3 Order Cancellation
WritingTools.AI reserves the right to refuse or cancel orders due to availability issues, pricing errors, or incorrect information.

## 8. Limitation of Liability

WritingTools.AI is not liable for any direct or indirect damages arising from the use of the site or service.

## 9. Indemnification

Users agree to indemnify and hold WritingTools.AI harmless from any claims or demands arising from website use, breach of agreement, or violation of rights.

## 10. Termination

### 10.1 Account Termination
WritingTools.AI reserves the right to terminate or suspend accounts at its discretion without prior notice or liability for any reason, including breach of these terms.

### 10.2 Agreement Termination
This agreement remains in effect until terminated by either party. Upon termination, you must cease all use of the website and delete any copies.

## 11. Changes to Terms

WritingTools.AI may modify these Terms of Service at any time without additional notice. Continued use of the service constitutes acceptance of the modified terms.

## 12. Dispute Resolution

### 12.1 Notice of Dispute
To initiate a dispute, send a written statement with your name, address, contact information, facts leading to the dispute, and requested relief to support@WritingTools.AI.

### 12.2 Informal Negotiation
Parties will attempt to resolve any dispute through informal negotiation within 60 days of receiving the Notice of Dispute.

### 12.3 Arbitration
If informal negotiation fails, the dispute will be resolved through arbitration in Finland.

## 13. Miscellaneous

### 13.1 Severability
If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full force and effect.

### 13.2 Waiver
Any waiver of these Terms must be in writing and signed by an authorized representative of WritingTools.AI.

### 13.3 Governing Law
These Terms shall be governed by and construed in accordance with the laws of Finland.

## 14. Contact Information

For any questions regarding these Terms of Service, please contact us at support@WritingTools.AI.

By using WritingTools.AI, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
`;

const PrivacyPolicy = () => {
  return (
    <>
      <PageHeadTags
        pageLink={serverRoutesConfig.terms}
        title="Terms & Conditions - WritingTools.AI"
        description="Terms & Conditions for WritingTools.AI"
      />

      <Container
        component="section"
        sx={{
          margin: "72px auto",
        }}
      >
        <Box
          sx={{
            marginBottom: "48px",
          }}
        >
          <MarkdownRenderer
            className="markdown-v3 blog-markdown"
            input={body}
          />
        </Box>
      </Container>
    </>
  );
};

export default PrivacyPolicy;

export async function getStaticProps() {
  return {
    props: {
      ...(await serverSideTranslations(locale, ["terms", "common"])),
      // Will be passed to the page component as props
    },
  };
}
